/**
 * /integrations — how CLEAR Talent fits the systems a buyer already runs.
 *
 * Every word the page renders comes from this file, and so does the route's
 * JSON-LD (`getSchemaForIntegrations`), so the FAQPage markup cannot restate an
 * answer the visitor does not see. The route is wired the same way as every
 * other public page: `/integrations` in `MarketingRoutes.tsx` and
 * `MarketingRoutes.server.tsx`, `INTEGRATIONS_PATH` in `prerenderRoutes`, and
 * SEO resolution in `seo/siteSeo.ts`.
 *
 * WHAT THIS PAGE MAY CLAIM. Only capabilities the product and the rest of the
 * site already state: API access, CSV and Excel bulk import of goals, ratings and
 * organisation structure, CSV/Excel/PDF export on every plan, DSAR exports,
 * automated stage reminders, and native iOS/Android apps with biometric sign-in
 * and push notifications. There is no prebuilt connector for any named HRIS, no
 * SAML single sign-on or SCIM provisioning, and no Slack or Microsoft Teams app —
 * the Ava chat prompt in `frontend/api/ava-chat.ts` says the same — so the page
 * says so plainly rather than leaving a buyer to assume. Nothing is labelled
 * "roadmap" because nothing in this repository commits to a date.
 *
 * Add a connector here only once it ships, and give it a single-integration
 * page (for example /integrations/microsoft-teams) only then.
 */
import { SCHEMA_CONTEXT, type JsonLd } from '../seo/structuredData';
import { breadcrumbListSchema, type BreadcrumbItem } from '../seo/breadcrumbs';

// Restated rather than imported from `seo/siteSeo.ts`, which imports this file.
const SITE_ORIGIN = 'https://www.clear-talent.com';
const ORG_ID = `${SITE_ORIGIN}/#organization`;
const WEBSITE_ID = `${SITE_ORIGIN}/#website`;
const SOFTWARE_ID = `${SITE_ORIGIN}/#software`;

export const INTEGRATIONS_PATH = '/integrations';

/**
 * The sitemap `<lastmod>` for the page. Move it forward when the visible content
 * changes — in particular when a connector ships — and not otherwise.
 */
export const INTEGRATIONS_LAST_MODIFIED = '2026-09-25';
export const INTEGRATIONS_LAST_MODIFIED_LABEL = '25 September 2026';

/**
 * `available`: shipped today. `not-available`: asked about often enough to
 * answer, and not something CLEAR Talent does today.
 */
export type IntegrationStatus = 'available' | 'not-available';

export interface IntegrationCapability {
  title: string;
  status: IntegrationStatus;
  /** 60–100 words on the workflow it enables, or on what to do instead. */
  body: string;
}

export interface IntegrationCategory {
  id: 'hris' | 'identity' | 'messaging' | 'data';
  eyebrow: string;
  title: string;
  intro: string;
  capabilities: IntegrationCapability[];
}

export interface IntegrationFaq {
  q: string;
  a: string;
}

export interface IntegrationsPage {
  path: string;
  seoTitle: string;
  metaDescription: string;
  eyebrow: string;
  h1: string;
  excerpt: string;
  intro: string[];
  categories: IntegrationCategory[];
  faqs: IntegrationFaq[];
  internalLinks: Array<{ label: string; href: string }>;
}

export const INTEGRATIONS_PAGE: IntegrationsPage = {
  path: INTEGRATIONS_PATH,
  seoTitle: 'Performance Management Integrations | CLEAR Talent',
  metaDescription:
    'How CLEAR Talent fits your HR stack: HRIS data in by CSV, Excel or API, reports out as CSV, Excel or PDF, and push reminders — plus what it does not do.',
  eyebrow: 'Integrations',
  h1: 'How CLEAR Talent fits the systems you already run',
  excerpt:
    'CLEAR Talent is a performance and development layer that sits beside your HRIS and payroll rather than replacing them. This page sets out exactly how data gets in and out today — and which connectors do not exist yet, so you can plan around them before the shortlist, not after the contract.',
  intro: [
    'The first question an HR director asks about any performance platform is whether it will fit the stack. The honest answer depends on what "fit" has to mean for you. If it means loading your people and structure, keeping them current and getting every record back out, CLEAR Talent does that today through bulk import, API access and export on every plan.',
    'If it means a prebuilt connector to a named HRIS, single sign-on through your identity provider, or an app inside Slack or Microsoft Teams, it does not — and we would rather you read that here than discover it in week three of an implementation.',
  ],
  categories: [
    {
      id: 'hris',
      eyebrow: 'HRIS and payroll',
      title: 'Your HRIS stays the system of record.',
      intro:
        'CLEAR Talent holds performance and development data. Employee records, payroll, time off and recruitment stay where they are.',
      capabilities: [
        {
          title: 'Bulk import from your HRIS export',
          status: 'available',
          body:
            'Export departments, locations, roles and reporting lines from the HRIS you already run, then load them through CSV or Excel bulk import before the first cycle opens. Doing it up front means the first round of feedback is about your process rather than about missing people. Departments, locations and reporting lines are first-class concepts in CLEAR Talent, so the structure you import is the structure reviews, goals and calibration run against — with multi-entity support where the organisation spans more than one company.',
        },
        {
          title: 'API access for structured data',
          status: 'available',
          body:
            'Where a file upload is not enough, API access lets your team move structured data such as goals, ratings and organisation structure programmatically. What transfers cleanly depends on how your current data is shaped, so the scope of any integration or migration is agreed during discovery rather than promised up front. Confirm which plan covers the API access you need before comparing quotes; the pricing page lists what each plan includes.',
        },
        {
          title: 'Staff transfers as people move',
          status: 'available',
          body:
            'Organisations do not hold still between cycles. When someone changes team, site or manager, a staff transfer moves them without rebuilding the structure, and their historical assessments stay in the employee record while the audit log captures the change. Combined with a fresh bulk import after a reorganisation or seasonal hiring, that keeps the org chart in CLEAR Talent close to the one in your HRIS without anyone re-keying records by hand.',
        },
        {
          title: 'Prebuilt HRIS connectors',
          status: 'not-available',
          body:
            'There is no prebuilt connector today for BambooHR, Workday, HiBob, Personio or any other named HRIS or payroll system, so nothing syncs automatically in the background. If your HRIS can export people and structure to CSV or Excel, that export is the route to plan around. If a live connection is a hard requirement, raise it in the first conversation: enterprise integrations are scoped per rollout, and you should know what that involves before you shortlist.',
        },
      ],
    },
    {
      id: 'identity',
      eyebrow: 'Identity and sign-in',
      title: 'Who can see what, and how people sign in.',
      intro:
        'Access control is built into the platform. Identity-provider integration is not, and IT teams should know that on day one.',
      capabilities: [
        {
          title: 'Role-based access with ownership checks',
          status: 'available',
          body:
            'Administrators, managers and employees each work within what their role allows, and access to an individual record is checked against who owns it rather than granted to everyone with a login. Access is logged, which is the part your security reviewer will ask about first. Because CLEAR Talent holds employee performance data rather than payroll or customer data, that review is narrower than it would be for a core HR system.',
        },
        {
          title: 'Biometric sign-in on mobile',
          status: 'available',
          body:
            'Employees who complete reviews and feedback on a phone use the native iOS and Android apps, which support biometric sign-in. That matters most for deskless and shift-based teams: a review that has to wait for a laptop and a remembered password is a review that arrives late. The same apps carry push notifications and offline support for sites where the signal drops, so completion does not depend on being at a desk.',
        },
        {
          title: 'SAML single sign-on and SCIM provisioning',
          status: 'not-available',
          body:
            'CLEAR Talent does not currently offer SAML single sign-on through an identity provider such as Microsoft Entra ID, Okta or Google Workspace, and there is no SCIM provisioning, so users are not created or removed automatically from your directory. People sign in with their own CLEAR Talent credentials. If your IT policy requires SSO for every business application, raise it with us before you shortlist rather than after.',
        },
      ],
    },
    {
      id: 'messaging',
      eyebrow: 'Messaging and notifications',
      title: 'Reminders reach people without a chat integration.',
      intro:
        'Most "integrate with Slack" requests are really about getting people to finish on time. That part is built in.',
      capabilities: [
        {
          title: 'Automated reminders on every stage',
          status: 'available',
          body:
            'Each stage of a review period — self-assessment, manager review, calibration, final rating — carries its own deadline, and the platform issues automated reminders on whatever is still outstanding. HR stops maintaining a side spreadsheet of who has not submitted, and managers stop sending chase emails. When a period closes, so does the window to amend it quietly, which keeps the record defensible without anyone policing it by hand.',
        },
        {
          title: 'Push notifications in the iOS and Android apps',
          status: 'available',
          body:
            'For employees who live on their phones rather than in an inbox, the native iOS and Android apps send push notifications when something needs their attention. The same apps cover assessments, feedback and one-to-one preparation, so the prompt and the task sit in one place instead of in an email pointing at a website. Offline support covers stockrooms, wards and sites where the connection drops, which is where completion usually stalls.',
        },
        {
          title: 'Slack and Microsoft Teams apps',
          status: 'not-available',
          body:
            'There is no CLEAR Talent app for Slack or Microsoft Teams today, so reviews, feedback and approvals are not completed inside either tool. Reminders and push notifications are how people are prompted instead. If your managers work almost entirely inside Teams or Slack, test the reminder and mobile journey with a few of them during the free trial, and tell us what you find before you decide.',
        },
      ],
    },
    {
      id: 'data',
      eyebrow: 'Data import, export and API',
      title: 'Getting records in, and getting them back out.',
      intro:
        'Your performance history is yours. Every plan includes export, and structured data can come in the same ways it goes out.',
      capabilities: [
        {
          title: 'CSV and Excel import of goals and ratings',
          status: 'available',
          body:
            'Beyond organisation structure, goals and ratings can be brought in through CSV and Excel bulk import, so a first cycle can start from the objectives already agreed rather than a blank page. Historic paper evidence is usually not worth re-keying: the platform starts building the record from the first period, and older files remain the record for the periods they cover. Migration scope is confirmed during discovery.',
        },
        {
          title: 'Export to CSV, Excel and PDF on every plan',
          status: 'available',
          body:
            'Every plan includes data export: records and reports come out as CSV, Excel or PDF, so a calibration summary can go to the board as a document and the underlying data can go to your analysts or back into the HRIS as a spreadsheet. Generated files are served through signed links that expire after 24 hours, so an export shared by mistake does not stay open indefinitely.',
        },
        {
          title: 'GDPR subject access and erasure requests',
          status: 'available',
          body:
            'When an employee exercises their data rights, the request should not become a manual search through review forms. CLEAR Talent supports data subject access request exports and right-to-be-forgotten workflows, so HR can answer a DSAR from the platform and handle an erasure request consistently. That keeps performance data inside the same governance you already apply to the rest of your people data, rather than in a spreadsheet nobody owns.',
        },
      ],
    },
  ],
  faqs: [
    {
      q: 'How long does setup take?',
      a: 'Rollout is measured in weeks. A guided onboarding wizard is tailored to organisation size, industry and performance-management maturity, and organisation structure, goals and ratings can be brought in through CSV and Excel bulk import or the API. Timing depends on organisation size, data migration, integrations and workflow complexity, so it is scoped with each customer before work begins.',
    },
    {
      q: 'Is employee data synced both ways?',
      a: 'No. There is no automatic two-way sync with an HRIS today. Your HRIS stays the system of record: people and structure go into CLEAR Talent through bulk import or API access, and performance records come back out as CSV, Excel or PDF exports on every plan. Enterprise integrations are scoped per rollout.',
    },
    {
      q: 'Does CLEAR Talent integrate with BambooHR, Workday, HiBob or Personio?',
      a: 'Not through a prebuilt connector. If your HRIS can export people and organisation structure to CSV or Excel, CLEAR Talent imports that file in bulk, and API access covers structured data where a file is not enough. If a live connection is a requirement, raise it in the first conversation so its scope is agreed before you commit.',
    },
    {
      q: 'Do you support single sign-on (SAML) or SCIM provisioning?',
      a: 'Not currently. People sign in with their own CLEAR Talent credentials, with biometric sign-in in the iOS and Android apps, and access is role-based with ownership checks and logging. If SSO is mandatory under your IT policy, tell us at the start of the evaluation.',
    },
    {
      q: 'Is there a Slack or Microsoft Teams integration?',
      a: 'No. Automated reminders on every review stage and push notifications in the native iOS and Android apps are how people are prompted to complete their part. Reviews, feedback and approvals are completed in CLEAR Talent on the web or in the apps.',
    },
    {
      q: 'Can we take our data with us if we leave?',
      a: 'Yes. Every plan includes data export, and records and reports can be taken out as CSV, Excel or PDF.',
    },
  ],
  internalLinks: [
    { label: 'Everything the platform does', href: '/features' },
    { label: 'Plans and what each includes', href: '/pricing' },
    { label: 'Trust and security centre', href: '/trust' },
    { label: 'Performance management solutions', href: '/solutions' },
    { label: 'Compare performance management platforms', href: '/best-performance-management-software' },
  ],
};

export const INTEGRATION_STATUS_LABEL: Record<IntegrationStatus, string> = {
  available: 'Available today',
  'not-available': 'Not available today',
};

export const getIntegrationsBreadcrumbs = (page: IntegrationsPage = INTEGRATIONS_PAGE): BreadcrumbItem[] => [
  { name: 'Home', path: '/' },
  { name: 'Integrations', path: page.path },
];

/** The route's nodes: WebPage, FAQPage and BreadcrumbList, all from the copy above. */
export const getSchemaForIntegrations = (page: IntegrationsPage = INTEGRATIONS_PAGE): JsonLd[] => {
  const url = `${SITE_ORIGIN}${page.path}`;

  return [
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'WebPage',
      '@id': `${url}#webpage`,
      url,
      name: page.seoTitle,
      description: page.metaDescription,
      isPartOf: { '@id': WEBSITE_ID },
      about: { '@id': SOFTWARE_ID },
      publisher: { '@id': ORG_ID },
      dateModified: INTEGRATIONS_LAST_MODIFIED,
      inLanguage: 'en',
    },
    {
      '@context': SCHEMA_CONTEXT,
      '@type': 'FAQPage',
      '@id': `${url}#faq`,
      mainEntity: page.faqs.map((faq) => ({
        '@type': 'Question',
        name: faq.q,
        acceptedAnswer: { '@type': 'Answer', text: faq.a },
      })),
    },
    breadcrumbListSchema(getIntegrationsBreadcrumbs(page)),
  ];
};
